import React, { useState } from 'react';

const DreamBuilderGame = ({ gameData, onComplete }) => {
    const [currentStageIndex, setCurrentStageIndex] = useState(-1); // -1 is intro
    const [selectedStoryParts, setSelectedStoryParts] = useState([]);
    const [score, setScore] = useState(0);

    const isOldFormat = gameData?.story && !gameData?.stages;
    const stages = isOldFormat ? [{ prompt: 'Ką nori daryti?', choices: gameData.story.choices }] : gameData?.stages;
    const introText = isOldFormat ? gameData.story.intro : gameData?.intro;

    const handleChoice = (choice) => {
        const storyChunk = isOldFormat ? choice.outcome : choice.story_part;
        const newParts = [...selectedStoryParts, { emoji: choice.emoji, text: storyChunk }];
        setSelectedStoryParts(newParts);
        
        playClickSound();
        if (navigator.vibrate) navigator.vibrate(40);

        if (currentStageIndex < stages.length - 1) {
            setCurrentStageIndex(prev => prev + 1);
        } else {
            setCurrentStageIndex(99); 
            const finalScore = gameData.points || 30;
            setScore(finalScore);
            playSuccessSound();
            if (navigator.vibrate) navigator.vibrate([100, 50, 100]);
        }
    };

    const finish = () => {
        onComplete?.(score);
    };

    const playClickSound = () => {
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        if (AudioContext) {
            const ctx = new AudioContext();
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.connect(gain);
            gain.connect(ctx.destination);
            osc.type = 'sine';
            osc.frequency.setValueAtTime(600, ctx.currentTime);
            osc.frequency.exponentialRampToValueAtTime(800, ctx.currentTime + 0.1);
            gain.gain.setValueAtTime(0.05, ctx.currentTime);
            gain.gain.exponentialRampToValueAtTime(0.00001, ctx.currentTime + 0.2);
            osc.start();
            osc.stop(ctx.currentTime + 0.2);
        }
    };

    const playSuccessSound = () => {
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        if (AudioContext) {
            const ctx = new AudioContext();
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.connect(gain);
            gain.connect(ctx.destination);
            osc.type = 'sine';
            osc.frequency.setValueAtTime(400, ctx.currentTime);
            osc.frequency.exponentialRampToValueAtTime(1200, ctx.currentTime + 0.3);
            gain.gain.setValueAtTime(0.1, ctx.currentTime);
            gain.gain.exponentialRampToValueAtTime(0.00001, ctx.currentTime + 0.5);
            osc.start();
            osc.stop(ctx.currentTime + 0.5);
        }
    };

    if (!gameData || (!gameData.story && !gameData.stages)) {
        return (
            <div className="premium-glass rounded-xl md:rounded-[3rem] p-4 md:p-12 text-center border-indigo-100 animate-pulse bg-slate-900 text-white">
                <div className="text-lg md:text-4xl md:text-[100px] mb-8">🌙</div>
                <h3 className="text-base md:text-3xl font-black tracking-tighter uppercase">Ruošiamas sapnas...</h3>
            </div>
        );
    }

    const isIntro = currentStageIndex === -1;
    const isOutcome = currentStageIndex === 99;
    const currentStage = !isIntro && !isOutcome ? stages[currentStageIndex] : null;
    const progress = isOutcome ? 100 : isIntro ? 0 : ((currentStageIndex) / stages.length) * 100;

    return (
        <div className="space-y-3 md:space-y-8 animate-slide-up select-none">
            {/* Premium Cosmic Header */}
            <div className="premium-glass rounded-lg md:rounded-[2.5rem] p-3 md:p-8 border-white/20 shadow-2xl relative overflow-hidden bg-indigo-950/80">
                <div className="absolute top-0 right-0 w-96 h-96 bg-fuchsia-500/10 rounded-full blur-[100px] pointer-events-none animate-pulse"></div>
                <div className="flex flex-col md:flex-row items-center justify-between gap-2 md:p-6 relative z-10">
                    <div className="flex items-center gap-4">
                        <div className="w-16 h-16 bg-indigo-900/50 rounded-2xl flex items-center justify-center text-base md:text-3xl shadow-inner border border-indigo-400/30 animate-float">
                            🌙
                        </div>
                        <div>
                            <h3 className="text-base md:text-3xl font-black text-white tracking-tight leading-tight">
                                Svajonių Kūrėjas
                            </h3>
                            <p className="text-indigo-300 font-bold">
                                Leiskis į nuotykį ir sukurk savo istoriją!
                            </p>
                        </div>
                    </div>

                    <div className="flex items-center gap-4">
                        <div className="bg-indigo-900/40 px-6 py-3 rounded-2xl border border-indigo-400/20 shadow-inner">
                            <p className="text-[10px] font-black text-indigo-400 uppercase tracking-widest leading-none mb-1">Vaizduotė</p>
                            <p className="text-2xl font-black text-fuchsia-400 leading-none">⭐ {score}</p>
                        </div>
                    </div>
                </div>

                <div className="mt-6 w-full bg-indigo-900/60 rounded-full h-3 p-1 shadow-inner overflow-hidden border border-indigo-800">
                    <div 
                        className="h-full bg-gradient-to-r from-fuchsia-500 via-purple-500 to-indigo-500 rounded-full transition-all duration-1000 shadow-[0_0_15px_rgba(217,70,239,0.5)]"
                        style={{ width: `${progress}%` }}
                    />
                </div>
            </div>

            {/* Dream Stage Area */}
            <div className="premium-glass rounded-2xl md:rounded-[4rem] p-4 md:p-10 lg:p-4 md:p-16 shadow-2xl border-white/10 bg-indigo-950/40 backdrop-blur-3xl relative overflow-hidden flex flex-col items-center min-h-[50vh] justify-center text-center">
                <div className="absolute top-0 right-0 p-4 md:p-12 opacity-5 text-2xl md:text-6xl md:text-[200px] font-black select-none pointer-events-none text-white">✨</div>
                <div className="absolute bottom-0 left-0 p-4 md:p-12 opacity-5 text-2xl md:text-6xl md:text-[200px] font-black select-none pointer-events-none text-white">☁️</div>

                <div className="w-full max-w-3xl relative z-10 space-y-4 md:space-y-12">
                    {isIntro && (
                        <div className="animate-slide-up space-y-4 md:space-y-12">
                            <div className="text-2xl md:text-6xl md:text-[180px] drop-shadow-[0_0_40px_rgba(255,255,255,0.2)] animate-float">✨</div>
                            <div className="space-y-6">
                                <span className="text-xs font-black text-fuchsia-400 uppercase tracking-[0.5em]">Pradžia</span>
                                <div className="relative inline-block">
                                    <span className="absolute -top-4 md:p-10 -left-10 text-9xl text-white/5 font-serif italic">"</span>
                                    <p className="text-base md:text-3xl md:text-lg md:text-4xl font-black text-white leading-tight font-serif italic relative z-10 px-8">
                                        {introText}
                                    </p>
                                    <span className="absolute -bottom-16 -right-10 text-9xl text-white/5 font-serif italic">"</span>
                                </div>
                            </div>
                            <button
                                onClick={() => setCurrentStageIndex(0)}
                                className="premium-button-3d group px-5 md:px-16 py-3 md:py-8 bg-gradient-to-r from-fuchsia-600 to-indigo-600 text-white rounded-xl md:rounded-[3rem] font-black text-2xl shadow-2xl shadow-fuchsia-900/40 uppercase tracking-widest relative overflow-hidden"
                            >
                                <div className="absolute inset-0 bg-white/20 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" />
                                <span className="relative z-10">PRADĖTI KELIONĘ 🚀</span>
                            </button>
                        </div>
                    )}

                    {currentStage && (
                        <div className="animate-slide-up space-y-4 md:space-y-12">
                            <div className="space-y-4">
                                <div className="inline-block bg-fuchsia-900/40 text-fuchsia-300 px-6 py-2 rounded-full text-xs font-black uppercase tracking-[0.4em] border border-fuchsia-800/50">Tavo Sprendimas</div>
                                <h3 className="text-lg md:text-4xl md:text-xl md:text-5xl font-black text-white tracking-tighter leading-tight drop-shadow-lg">
                                    {currentStage.prompt}
                                </h3>
                            </div>

                            <div className="grid grid-cols-1 gap-2 md:p-6 w-full max-w-2xl mx-auto">
                                {currentStage.choices.map((choice, idx) => (
                                    <button
                                        key={idx}
                                        onClick={() => handleChoice(choice)}
                                        className="premium-button-3d flex items-center gap-3 md:p-8 p-3 md:p-8 bg-indigo-900/40 hover:bg-fuchsia-900/60 rounded-xl md:rounded-[3rem] border border-indigo-400/20 text-left transition-all duration-300 group shadow-2xl"
                                    >
                                        <div className="w-20 h-20 bg-white/10 rounded-[2rem] flex items-center justify-center text-xl md:text-5xl shrink-0 group-hover:scale-110 group-hover:bg-white/20 transition-all shadow-inner border border-white/5">
                                            {choice.emoji}
                                        </div>
                                        <div className="flex-1">
                                            <span className="text-2xl lg:text-base md:text-3xl font-black text-indigo-50 group-hover:text-white transition-colors leading-tight block mb-1">
                                                {choice.text}
                                            </span>
                                            <span className="text-[10px] font-black text-fuchsia-400 uppercase tracking-widest opacity-0 group-hover:opacity-100 transition-opacity">Pasirinkti šį kelią</span>
                                        </div>
                                        <div className="text-base md:text-3xl text-fuchsia-400 opacity-0 group-hover:opacity-100 group-hover:translate-x-4 transition-all">➔</div>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {isOutcome && (
                        <div className="animate-slide-up space-y-4 md:space-y-12">
                            <div className="inline-block bg-emerald-500/20 border border-emerald-400/50 text-emerald-300 font-black uppercase tracking-[0.5em] px-8 py-3 rounded-full shadow-[0_0_20px_rgba(16,185,129,0.3)] backdrop-blur-sm">
                                Tavo Sapno Istorija
                            </div>

                            <div className="bg-indigo-950/60 backdrop-blur-2xl rounded-2xl md:rounded-[4rem] border-2 border-white/10 p-4 md:p-10 lg:p-4 md:p-16 shadow-[0_40px_100px_rgba(0,0,0,0.5)] text-left space-y-4 md:space-y-10 relative overflow-hidden">
                                <div className="absolute top-0 right-0 p-4 md:p-12 text-9xl opacity-[0.02] text-white italic font-serif">Story</div>
                                
                                <p className="text-base md:text-3xl text-indigo-200 italic font-serif leading-relaxed px-4">
                                    "{introText}"
                                </p>

                                <div className="space-y-6">
                                    {selectedStoryParts.map((part, idx) => (
                                        <div key={idx} className="flex gap-3 md:p-8 items-center bg-white/5 rounded-xl md:rounded-[3rem] p-3 md:p-8 hover:bg-white/10 transition-all border border-white/5 group">
                                            <span className="text-2xl md:text-6xl group-hover:scale-110 transition-transform drop-shadow-xl shrink-0">{part.emoji}</span>
                                            <p className="text-2xl md:text-base md:text-3xl font-black text-white leading-snug tracking-tight">
                                                {part.text}
                                            </p>
                                        </div>
                                    ))}
                                </div>

                                {gameData.conclusion && (
                                    <div className="pt-8 text-center">
                                        <p className="text-2xl text-fuchsia-400 font-black italic font-serif drop-shadow-[0_0_10px_rgba(232,121,249,0.3)]">
                                            ~ {gameData.conclusion} ~
                                        </p>
                                    </div>
                                )}
                            </div>

                            <button
                                onClick={finish}
                                className="premium-button-3d group px-5 md:px-16 py-3 md:py-8 bg-gradient-to-r from-emerald-500 to-teal-500 text-slate-900 rounded-xl md:rounded-[3rem] font-black text-2xl transition-all duration-300 shadow-2xl shadow-emerald-900/40 uppercase tracking-widest relative overflow-hidden"
                            >
                                <div className="absolute inset-0 bg-white/30 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" />
                                <span className="relative z-10 flex items-center gap-4">
                                    PABUSTI ✨ <span className="text-sm opacity-50 font-black">BAIGTI</span>
                                </span>
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default DreamBuilderGame;
